import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, Copy, RefreshCw, ExternalLink } from 'lucide-react';

interface DNSRecord {
  type: string;
  name: string;
  value: string;
  description: string;
}

interface DomainVerificationPanelProps {
  connectionId: number;
  emailAddress: string;
  domainVerified: boolean;
  domainVerificationStatus: string;
  onVerificationComplete?: () => void;
}

export const DomainVerificationPanel: React.FC<DomainVerificationPanelProps> = ({
  connectionId,
  emailAddress,
  domainVerified,
  domainVerificationStatus,
  onVerificationComplete,
}) => {
  const [dnsRecords, setDnsRecords] = useState<DNSRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [verificationResult, setVerificationResult] = useState<any>(null);

  const domain = emailAddress.split('@')[1];

  useEffect(() => {
    if (!domainVerified) {
      fetchDNSRecords();
    }
  }, [connectionId, domainVerified]);

  const fetchDNSRecords = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/email/connections/${connectionId}/dns-records`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch DNS records');
      }

      const data = await response.json();
      setDnsRecords(data.dns_records || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const verifyDomain = async () => {
    setVerifying(true);
    setError(null);
    setVerificationResult(null);
    try {
      const response = await fetch(`/api/email/connections/${connectionId}/verify-domain`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to verify domain');
      }

      const data = await response.json();
      setVerificationResult(data);

      if (data.verified && onVerificationComplete) {
        onVerificationComplete();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setVerifying(false);
    }
  };

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  if (domainVerified) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start">
          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 mr-3" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-green-900">Domaine vérifié</h3>
            <p className="text-sm text-green-700 mt-1">
              Le domaine <strong>{domain}</strong> est authentifié dans SendGrid. Vous pouvez maintenant envoyer des emails depuis <strong>{emailAddress}</strong>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-yellow-900">Configuration DNS requise</h3>
            <p className="text-sm text-yellow-700 mt-1">
              Pour envoyer des emails depuis <strong>{emailAddress}</strong>, vous devez configurer les enregistrements DNS suivants pour le domaine <strong>{domain}</strong>.
            </p>
          </div>
        </div>
      </div>

      {/* DNS Records */}
      {loading ? (
        <div className="text-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" />
          <p className="text-sm text-gray-500 mt-2">Chargement des enregistrements DNS...</p>
        </div>
      ) : dnsRecords.length > 0 ? (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-900">Enregistrements DNS à ajouter :</h4>
          {dnsRecords.map((record, index) => (
            <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {record.type}
                  </span>
                  <span className="ml-2 text-sm text-gray-600">{record.description}</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Nom</label>
                  <div className="flex items-center">
                    <code className="flex-1 bg-gray-50 px-3 py-2 rounded text-sm font-mono text-gray-900 break-all">
                      {record.name}
                    </code>
                    <button
                      onClick={() => copyToClipboard(record.name, `name-${index}`)}
                      className="ml-2 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Copier"
                    >
                      {copiedField === `name-${index}` ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Valeur</label>
                  <div className="flex items-center">
                    <code className="flex-1 bg-gray-50 px-3 py-2 rounded text-sm font-mono text-gray-900 break-all">
                      {record.value}
                    </code>
                    <button
                      onClick={() => copyToClipboard(record.value, `value-${index}`)}
                      className="ml-2 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Copier"
                    >
                      {copiedField === `value-${index}` ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">Instructions</h4>
        <ol className="text-sm text-blue-700 space-y-2 list-decimal list-inside">
          <li>Connectez-vous à votre fournisseur DNS (ex: Cloudflare, OVH, Gandi, etc.)</li>
          <li>Ajoutez les enregistrements DNS ci-dessus dans la zone DNS de <strong>{domain}</strong></li>
          <li>Attendez quelques minutes pour la propagation DNS (peut prendre jusqu'à 48h)</li>
          <li>Cliquez sur "Vérifier le domaine" ci-dessous</li>
        </ol>
        <a
          href="https://docs.sendgrid.com/ui/account-and-settings/how-to-set-up-domain-authentication"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 mt-3"
        >
          <ExternalLink className="w-4 h-4 mr-1" />
          Documentation SendGrid
        </a>
      </div>

      {/* Verification Result */}
      {verificationResult && !verificationResult.verified && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-red-900">Vérification échouée</h4>
              <p className="text-sm text-red-700 mt-1">
                Les enregistrements DNS ne sont pas encore correctement configurés. Veuillez vérifier que vous avez bien ajouté tous les enregistrements et attendez quelques minutes pour la propagation.
              </p>
              {verificationResult.validation_results && (
                <details className="mt-2">
                  <summary className="text-xs text-red-600 cursor-pointer hover:text-red-800">
                    Détails techniques
                  </summary>
                  <pre className="mt-2 text-xs bg-red-100 p-2 rounded overflow-auto">
                    {JSON.stringify(verificationResult.validation_results, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      )}

      {verificationResult && verificationResult.verified && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 mr-3" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-green-900">Domaine vérifié !</h4>
              <p className="text-sm text-green-700 mt-1">
                Le domaine <strong>{domain}</strong> est maintenant authentifié. Vous pouvez envoyer des emails depuis <strong>{emailAddress}</strong>.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-red-900">Erreur</h4>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <button
          onClick={fetchDNSRecords}
          disabled={loading}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
        
        <button
          onClick={verifyDomain}
          disabled={verifying || loading}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {verifying ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Vérification...
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4 mr-2" />
              Vérifier le domaine
            </>
          )}
        </button>
      </div>
    </div>
  );
};
